export const IANA_TIMEZONES: string[] =
  typeof Intl !== "undefined" &&
  typeof (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf === "function"
    ? (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf(
        "timeZone"
      )
    : ["UTC"];

export const IANA_TIMEZONE_SET = new Set(IANA_TIMEZONES);

/**
 * Legacy zone ids some browsers still report via `Intl` but strict IANA validators reject.
 * @see https://unicode.org/reports/tr35/tr35-dates.html#Time_Zone_Fallback
 */
export const LEGACY_IANA_TIMEZONE_ALIASES: Record<string, string> = {
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Europe/Kiev": "Europe/Kyiv",
  "Europe/Uzhgorod": "Europe/Uzhhorod",
  "Europe/Zaporozhye": "Europe/Zaporizhzhia",
  "America/Godthab": "America/Nuuk",
  "Pacific/Truk": "Pacific/Chuuk",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Africa/Asmera": "Africa/Asmara",
};

/** Map browser/legacy ids to canonical IANA names accepted by the API. */
export function normalizeIanaTimeZone(timeZone: string): string {
  const trimmed = timeZone.trim();
  if (!trimmed) return "UTC";

  const directAlias = LEGACY_IANA_TIMEZONE_ALIASES[trimmed];
  if (directAlias) return directAlias;

  const lower = trimmed.toLowerCase();
  for (const [legacy, canonical] of Object.entries(LEGACY_IANA_TIMEZONE_ALIASES)) {
    if (legacy.toLowerCase() === lower) return canonical;
  }

  if (IANA_TIMEZONE_SET.has(trimmed)) return trimmed;

  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch {
    return "UTC";
  }
}

/** Minutes east of UTC at `at` (DST-aware). */
export function getIanaTimezoneOffsetMinutes(timeZone: string, at: Date): number {
  if (timeZone === "UTC") return 0;
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts: Record<string, string> = {};
    for (const p of formatter.formatToParts(at)) {
      if (p.type !== "literal") parts[p.type] = p.value;
    }
    const wallAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((wallAsUtc - at.getTime()) / 60000);
  } catch {
    return 0;
  }
}

function compareIanaTimezonesByUtcOffset(a: string, b: string, at: Date): number {
  const offsetDiff = getIanaTimezoneOffsetMinutes(a, at) - getIanaTimezoneOffsetMinutes(b, at);
  if (offsetDiff !== 0) return offsetDiff;
  return a.localeCompare(b);
}

function humanizeIanaSegment(segment: string): string {
  return segment.replace(/_/g, " ").trim();
}

/** UTC±HH:MM for `timeZone` at `at` (DST-aware). */
export function formatTimezoneUtcOffsetLabel(timeZone: string, at: Date): string {
  if (timeZone === "UTC") return "UTC+00:00";
  try {
    const dtf = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      timeZoneName: "longOffset",
    });
    const parts = dtf.formatToParts(at);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value;
    if (!raw) return "UTC";
    const normalized = raw.replace(/^GMT/i, "UTC").trim();
    if (normalized === "UTC") return "UTC+00:00";
    return normalized;
  } catch {
    return "UTC";
  }
}

/**
 * City / area for display — spaces instead of underscores.
 * e.g. America/New_York → "New York, America"
 */
export function formatTimezonePlaceLabel(timeZone: string): string {
  const trimmed = timeZone.trim();
  if (!trimmed || trimmed === "UTC") return "UTC";

  const parts = trimmed.split("/").map(humanizeIanaSegment).filter(Boolean);
  if (parts.length === 0) return trimmed;
  if (parts.length === 1) return parts[0];

  // Etc/GMT+12 → "GMT+12" (offset already shown separately)
  if (parts[0] === "Etc") {
    return parts.slice(1).join(" ") || "UTC";
  }

  if (parts.length === 2) {
    return `${parts[1]}, ${parts[0]}`;
  }

  // America/Argentina/Buenos_Aires → "Buenos Aires, Argentina"
  const city = parts[parts.length - 1];
  const area = parts[parts.length - 2];
  return `${city}, ${area}`;
}

/**
 * Display label: offset first, then place.
 * e.g. "UTC-05:00 · New York, America"
 * Submitted value remains the IANA id.
 */
export function formatTimezoneSelectLabel(timeZone: string, at: Date): string {
  const offset = formatTimezoneUtcOffsetLabel(timeZone, at);
  const place = formatTimezonePlaceLabel(timeZone);
  return `${offset} · ${place}`;
}

export type TimezoneSelectOption = {
  value: string;
  /** Full line for the closed field, e.g. "UTC-05:00 · New York, America". */
  label: string;
  offsetLabel: string;
  placeLabel: string;
};

function allIanaTimezonesForSelect(): string[] {
  if (IANA_TIMEZONE_SET.has("UTC")) {
    return [...IANA_TIMEZONES];
  }
  return ["UTC", ...IANA_TIMEZONES];
}

/** IANA zones sorted by current UTC offset; labels are offset-first with humanized places. */
export function buildTimezoneSelectOptions(at: Date = new Date()): TimezoneSelectOption[] {
  return allIanaTimezonesForSelect()
    .sort((a, b) => compareIanaTimezonesByUtcOffset(a, b, at))
    .map((tz) => {
      const offsetLabel = formatTimezoneUtcOffsetLabel(tz, at);
      const placeLabel = formatTimezonePlaceLabel(tz);
      return {
        value: tz,
        offsetLabel,
        placeLabel,
        label: `${offsetLabel} · ${placeLabel}`,
      };
    });
}

/** Text searchable from IANA id + offset + place (e.g. "new york", "sofia", "utc+02"). */
export function timezoneOptionSearchText(option: TimezoneSelectOption): string {
  const id = option.value;
  return [
    id,
    id.replace(/_/g, " "),
    option.label,
    option.offsetLabel,
    option.placeLabel,
  ].join(" ");
}

/**
 * Filter timezone options by city / region / offset. Empty query returns the full list.
 */
export function filterTimezoneSelectOptions(
  options: TimezoneSelectOption[],
  query: string
): TimezoneSelectOption[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return options;

  const tokens = trimmed
    .replace(/[·•]/g, " ")
    .replace(/,/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  return options.filter((option) => {
    const haystack = timezoneOptionSearchText(option).toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}
